import {
    AlertTriangle,
    CheckCircle,
    ChevronDown,
    ChevronRight,
    ClipboardCopy,
    History,
    KeyRound,
    Loader2,
    Lock,
    Mail,
    MessageSquare,
    RefreshCw,
    Search,
    ShieldAlert,
    Unlock,
    X
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import apiService from '../services/apiService';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('it-IT', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

function StatusBadge({ status }) {
    const MAP = {
        ACTIVE: { label: 'Attivo', color: '#16a34a', bg: '#dcfce7' },
        SHIPPED_PENDING_ACTIVATION: { label: 'Spedito', color: '#d97706', bg: '#fef9c3' },
        PROVISIONED: { label: 'Provisionato', color: '#2563eb', bg: '#dbeafe' },
        DISABLED: { label: 'Disabilitato', color: '#dc2626', bg: '#fee2e2' },
    };
    const m = MAP[status] || { label: status, color: '#6b7280', bg: '#f3f4f6' };
    return (
        <span
            title={status ? `Status originale: ${status}` : 'Status originale non disponibile'}
            style={{
                background: m.bg, color: m.color,
                padding: '2px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 600,
            }}
        >{m.label}</span>
    );
}

function ErrorBox({ message }) {
    if (!message) return null;
    return (
        <div style={{
            background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b',
            padding: '10px 14px', borderRadius: 8, marginBottom: 14, display: 'flex',
            alignItems: 'flex-start', gap: 8,
        }}>
            <AlertTriangle size={16} style={{ marginTop: 1, flexShrink: 0 }} />
            <span style={{ fontSize: 13 }}>{message}</span>
        </div>
    );
}

function SuccessBox({ message }) {
    if (!message) return null;
    return (
        <div style={{
            background: '#dcfce7', border: '1px solid #86efac', color: '#166534',
            padding: '10px 14px', borderRadius: 8, marginBottom: 14, display: 'flex',
            alignItems: 'flex-start', gap: 8,
        }}>
            <CheckCircle size={16} style={{ marginTop: 1, flexShrink: 0 }} />
            <span style={{ fontSize: 13 }}>{message}</span>
        </div>
    );
}

// ─── Confirm Modal ────────────────────────────────────────────────────────────

function ConfirmModal({ title, description, requireReason, extraFields = [], onConfirm, onClose, loading, confirmLabel = 'Conferma', confirmColor = '#dc2626' }) {
    const [reason, setReason] = useState('');
    const [extras, setExtras] = useState({});
    const [validationErr, setValidationErr] = useState('');

    const handleConfirm = () => {
        if (requireReason && !reason.trim()) {
            setValidationErr('Il campo "Motivazione" è obbligatorio.');
            return;
        }
        setValidationErr('');
        onConfirm({ reason: reason.trim(), ...extras });
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 480, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{title}</h3>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><X size={18} /></button>
                </div>
                {description && <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>{description}</p>}

                <ErrorBox message={validationErr} />

                {requireReason && (
                    <div style={{ marginBottom: 14 }}>
                        <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                            Motivazione <span style={{ color: '#dc2626' }}>*</span>
                        </label>
                        <textarea
                            rows={3}
                            placeholder="Es. Cliente bloccato durante attivazione tablet..."
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
                        />
                    </div>
                )}

                {extraFields.map((f) => (
                    <div key={f.name} style={{ marginBottom: 14 }}>
                        <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                            {f.label} {f.required && <span style={{ color: '#dc2626' }}>*</span>}
                        </label>
                        <input
                            type={f.type || 'text'}
                            placeholder={f.placeholder || ''}
                            value={extras[f.name] || ''}
                            onChange={(e) => setExtras((p) => ({ ...p, [f.name]: e.target.value }))}
                            style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                        />
                    </div>
                ))}

                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
                    <button onClick={onClose} disabled={loading} style={{ padding: '8px 18px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Annulla</button>
                    <button onClick={handleConfirm} disabled={loading} style={{ padding: '8px 18px', borderRadius: 6, border: 'none', background: confirmColor, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                        {loading && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Send Instructions Modal ─────────────────────────────────────────────────

function SendInstructionsModal({ device, sessionPin, loading, onConfirm, onClose }) {
    const buildTemplate = (m) => {
        const sc = device.setupCode || '—';
        const label = device.label || device.setupCode || '—';
        const venue = device.venue?.name || '';
        const pin = sessionPin || null;
        const expiry = device.activationExpiry ? fmtDate(device.activationExpiry) : null;

        if (m === 'sms') {
            return [
                `SoonTip — Attivazione tablet`,
                `Dispositivo: ${label}`,
                `Setup Code: ${sc}`,
                pin ? `PIN: ${pin}` : `PIN: [rigenerare dalla console]`,
                expiry ? `Scade: ${expiry}` : null,
                ``,
                `Istruzioni: aprire app SoonTip, inserire Setup Code e PIN.`,
                `Assistenza: supporto@soontip.com`,
            ].filter(Boolean).join('\n');
        }
        return [
            venue ? `Gentile team di ${venue},` : 'Gentile team,',
            ``,
            `di seguito le credenziali per l'attivazione del dispositivo SoonTip.`,
            ``,
            `Dispositivo: ${label}`,
            venue ? `Venue: ${venue}` : null,
            ``,
            `━━━━━━━━━━━━━━━━━━━━━`,
            `Setup Code:  ${sc}`,
            pin ? `PIN:         ${pin}` : `PIN:         [rigenerare dalla console admin]`,
            expiry ? `Scadenza:    ${expiry}` : null,
            `━━━━━━━━━━━━━━━━━━━━━`,
            ``,
            `Istruzioni:`,
            `1. Aprire l'app SoonTip sul tablet`,
            `2. Inserire il Setup Code: ${sc}`,
            pin ? `3. Inserire il PIN: ${pin}` : `3. Inserire il PIN quando comunicato`,
            `4. Il dispositivo sarà operativo`,
            ``,
            `Per assistenza: supporto@soontip.com`,
        ].filter((l) => l !== null).join('\n');
    };

    const [mode, setMode] = useState('email');
    const [recipient, setRecipient] = useState('');
    const [phone, setPhone] = useState('');
    const [reason, setReason] = useState('');
    const [messageText, setMessageText] = useState(() => buildTemplate('email'));
    const [validationErr, setValidationErr] = useState('');
    const [copied, setCopied] = useState(false);

    const handleModeChange = (newMode) => {
        setMode(newMode);
        setMessageText(buildTemplate(newMode));
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(messageText).catch(() => { });
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleConfirm = () => {
        if (!reason.trim()) { setValidationErr('La motivazione è obbligatoria.'); return; }
        if (mode === 'email' && !recipient.trim()) { setValidationErr("Inserisci l'indirizzo email del destinatario."); return; }
        setValidationErr('');
        onConfirm({ reason: reason.trim(), deliveryMode: mode, recipientEmail: mode === 'email' ? recipient : undefined, phone: mode === 'sms' ? phone : undefined, messageText });
    };

    const smsLink = phone ? `sms:${phone.replace(/\s/g, '')}?body=${encodeURIComponent(messageText)}` : null;

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 560, width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Invia Istruzioni Attivazione</h3>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><X size={18} /></button>
                </div>

                {/* Mode toggle — SMS tab hidden until Twilio Programmable Messaging is configured */}
                {/* To re-enable SMS: uncomment the SMS button below */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                    <button
                        onClick={() => handleModeChange('email')}
                        style={{ flex: 1, padding: '9px 0', borderRadius: 7, border: `2px solid ${mode === 'email' ? '#2563eb' : '#e5e7eb'}`, background: mode === 'email' ? '#dbeafe' : '#f9fafb', color: mode === 'email' ? '#1d4ed8' : '#6b7280', fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                    >
                        <Mail size={14} /> Email
                    </button>
                    {/* <button
                        onClick={() => handleModeChange('sms')}
                        style={{ flex: 1, padding: '9px 0', borderRadius: 7, border: `2px solid ${mode === 'sms' ? '#16a34a' : '#e5e7eb'}`, background: mode === 'sms' ? '#dcfce7' : '#f9fafb', color: mode === 'sms' ? '#15803d' : '#6b7280', fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                    >
                        <MessageSquare size={14} /> SMS
                    </button> */}
                </div>

                {!sessionPin && (
                    <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', color: '#92400e', padding: '9px 12px', borderRadius: 7, fontSize: 12, marginBottom: 14, display: 'flex', gap: 6 }}>
                        <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                        Nessun PIN generato in questa sessione. Il testo include un segnaposto. Rigenera prima il PIN per includere il valore reale.
                    </div>
                )}

                <ErrorBox message={validationErr} />

                {/* Recipient */}
                {mode === 'email' && (
                    <div style={{ marginBottom: 14 }}>
                        <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Email destinatario <span style={{ color: '#dc2626' }}>*</span></label>
                        <input type="email" placeholder="referente@venue.com" value={recipient} onChange={(e) => setRecipient(e.target.value)} style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                    </div>
                )}
                {mode === 'sms' && (
                    <div style={{ marginBottom: 14 }}>
                        <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Numero di telefono <span style={{ color: '#6b7280', fontWeight: 400 }}>(opzionale — per aprire SMS app)</span></label>
                        <input type="tel" placeholder="+39 333 1234567" value={phone} onChange={(e) => setPhone(e.target.value)} style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                    </div>
                )}

                {/* Reason */}
                <div style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Motivazione <span style={{ color: '#dc2626' }}>*</span></label>
                    <input type="text" placeholder="Es. Supporto attivazione su richiesta cliente" value={reason} onChange={(e) => setReason(e.target.value)} style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                </div>

                {/* Editable message */}
                <div style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                        Testo del messaggio <span style={{ color: '#6b7280', fontWeight: 400 }}>(modificabile)</span>
                    </label>
                    <textarea
                        rows={mode === 'sms' ? 8 : 14}
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        style={{ width: '100%', padding: '9px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.6 }}
                    />
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={handleCopy} style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #d1d5db', background: '#f9fafb', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                            <ClipboardCopy size={13} /> {copied ? 'Copiato!' : 'Copia testo'}
                        </button>
                        {mode === 'sms' && smsLink && (
                            <a href={smsLink} style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #16a34a', background: '#dcfce7', color: '#15803d', fontSize: 13, fontWeight: 600, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5 }}>
                                <MessageSquare size={13} /> Apri SMS app
                            </a>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={onClose} disabled={loading} style={{ padding: '8px 18px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Annulla</button>
                        {mode === 'email' && (
                            <button onClick={handleConfirm} disabled={loading} style={{ padding: '8px 18px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                                {loading && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
                                <Mail size={13} /> Invia Email
                            </button>
                        )}
                        {mode === 'sms' && (
                            <button onClick={handleConfirm} disabled={loading} style={{ padding: '8px 18px', borderRadius: 6, border: 'none', background: '#16a34a', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                                {loading && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
                                <MessageSquare size={13} /> Registra invio SMS
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── PIN one-time display ─────────────────────────────────────────────────────

function PinRevealBox({ pin, setupCode, expiresAt, onClose }) {
    const [copied, setCopied] = useState(false);

    const copyAll = () => {
        navigator.clipboard.writeText(`Setup Code: ${setupCode}\nPIN: ${pin}`).catch(() => { });
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div style={{ background: '#fffbeb', border: '2px solid #f59e0b', borderRadius: 10, padding: 20, marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <ShieldAlert size={18} color="#d97706" />
                <strong style={{ color: '#92400e', fontSize: 14 }}>PIN visibile solo ora — non sarà più recuperabile</strong>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div style={{ background: '#fff', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 14px' }}>
                    <div style={{ fontSize: 11, color: '#92400e', fontWeight: 600, marginBottom: 4 }}>SETUP CODE</div>
                    <div style={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 700, letterSpacing: 2 }}>{setupCode}</div>
                </div>
                <div style={{ background: '#fff', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 14px' }}>
                    <div style={{ fontSize: 11, color: '#92400e', fontWeight: 600, marginBottom: 4 }}>PIN ATTIVAZIONE</div>
                    <div style={{ fontFamily: 'monospace', fontSize: 28, fontWeight: 700, letterSpacing: 4, color: '#dc2626' }}>{pin}</div>
                </div>
            </div>
            {expiresAt && <div style={{ fontSize: 12, color: '#78350f', marginBottom: 12 }}>Scade: {fmtDate(expiresAt)}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={copyAll} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    <ClipboardCopy size={14} /> {copied ? 'Copiato!' : 'Copia tutto'}
                </button>
                <button onClick={onClose} style={{ padding: '7px 14px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Chiudi</button>
            </div>
        </div>
    );
}

// ─── History Panel ────────────────────────────────────────────────────────────

function HistoryPanel({ deviceId, onClose }) {
    const [history, setHistory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expanded, setExpanded] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await apiService.getDeviceActivationHistory(deviceId);
            setHistory(data.history || []);
        } catch {
            setError('Impossibile caricare la cronologia.');
        } finally {
            setLoading(false);
        }
    }, [deviceId]);

    useState(() => { load(); }, [load]);

    const ACTION_LABELS = {
        DEVICE_ACTIVATION_REGENERATED: { label: 'Rigenerazione PIN', color: '#2563eb' },
        DEVICE_ACTIVATION_REGENERATE_FAILED: { label: 'Rigenerazione fallita', color: '#dc2626' },
        DEVICE_ACTIVATION_LOCKOUT_RESET: { label: 'Reset lockout', color: '#16a34a' },
        DEVICE_ACTIVATION_INSTRUCTIONS_SENT: { label: 'Istruzioni inviate', color: '#7c3aed' },
        DEVICE_ACTIVATION_INSTRUCTIONS_FAILED: { label: 'Invio fallito', color: '#dc2626' },
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 640, width: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <History size={18} /> Cronologia supporto attivazione
                    </h3>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><X size={18} /></button>
                </div>

                <ErrorBox message={error} />

                <div style={{ overflowY: 'auto', flex: 1 }}>
                    {loading && <div style={{ textAlign: 'center', padding: 32, color: '#6b7280' }}><Loader2 size={22} style={{ animation: 'spin 1s linear infinite' }} /></div>}
                    {!loading && history && history.length === 0 && <p style={{ color: '#6b7280', fontSize: 13, textAlign: 'center', padding: 24 }}>Nessuna operazione di supporto registrata.</p>}
                    {!loading && history && history.map((log) => {
                        const meta = ACTION_LABELS[log.action] || { label: log.action, color: '#6b7280' };
                        const isOpen = expanded[log.id];
                        return (
                            <div key={log.id} style={{ borderBottom: '1px solid #f3f4f6', padding: '12px 0' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ background: meta.color + '20', color: meta.color, padding: '2px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 600 }}>{meta.label}</span>
                                        <span style={{ fontSize: 12, color: '#6b7280' }}>{fmtDate(log.createdAt)}</span>
                                    </div>
                                    {log.metadata && Object.keys(log.metadata).length > 0 && (
                                        <button onClick={() => setExpanded((p) => ({ ...p, [log.id]: !p[log.id] }))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
                                            {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                        </button>
                                    )}
                                </div>
                                {log.metadata?.reason && <div style={{ fontSize: 12, color: '#374151', marginTop: 4, paddingLeft: 2 }}>Motivazione: <em>{log.metadata.reason}</em></div>}
                                {isOpen && (
                                    <pre style={{ margin: '8px 0 0', padding: '8px 12px', background: '#f9fafb', borderRadius: 6, fontSize: 11, overflow: 'auto' }}>
                                        {JSON.stringify(log.metadata, null, 2)}
                                    </pre>
                                )}
                            </div>
                        );
                    })}
                </div>

                <button onClick={onClose} style={{ marginTop: 16, padding: '8px 18px', background: '#f3f4f6', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, alignSelf: 'flex-end' }}>Chiudi</button>
            </div>
        </div>
    );
}

// ─── Device Detail Panel ──────────────────────────────────────────────────────

function DeviceDetailPanel({ device, onClose, onRefresh }) {
    const [modal, setModal] = useState(null); // 'regenerate' | 'reset-lockout' | 'send-instructions'
    const [actionLoading, setActionLoading] = useState(false);
    const [pinReveal, setPinReveal] = useState(null); // { pin, setupCode, expiresAt }
    const [sessionPin, setSessionPin] = useState(null); // PIN from last regen (for send-instructions)
    const [actionErr, setActionErr] = useState('');
    const [actionOk, setActionOk] = useState('');
    const [showHistory, setShowHistory] = useState(false);
    const [currentDevice, setCurrentDevice] = useState(device);

    useEffect(() => {
        setCurrentDevice(device);
    }, [device]);

    const activeDevice = currentDevice || device;

    const isLocked = activeDevice.isLocked || (activeDevice.lockedUntil && new Date() < new Date(activeDevice.lockedUntil));
    const isDisabled = activeDevice.activationStatus === 'DISABLED';
    const isPinExpired = activeDevice.setupCodeStatus === 'PIN_EXPIRED';
    const pinExpiry = activeDevice.activationExpiry || activeDevice.setupCodeExpiry;

    const handleRegenerate = async ({ reason }) => {
        setActionLoading(true);
        setActionErr('');
        try {
            const data = await apiService.regenerateDeviceActivation(activeDevice.deviceId, reason, 'show_once');
            setSessionPin(data.pin);
            setPinReveal({ pin: data.pin, setupCode: data.setupCode, expiresAt: data.expiresAt });
            setCurrentDevice((prev) => ({
                ...prev,
                setupCodeStatus: 'PIN_PENDING',
                activationExpiry: data.expiresAt,
                setupCodeExpiry: data.expiresAt,
                failedAttempts: 0,
                activationAttempts: 0,
                isLocked: false,
                lockedUntil: null,
            }));
            setActionOk('Credenziali rigenerate con successo.');
            setModal(null);
            // onRefresh() è chiamato alla chiusura del PIN box per evitare
            // che il remount del panel azzeri pinReveal prima che sia letto
        } catch (e) {
            setActionErr(e?.response?.data?.error || 'Rigenerazione fallita.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleResetLockout = async ({ reason }) => {
        setActionLoading(true);
        setActionErr('');
        try {
            await apiService.resetDeviceActivationLockout(activeDevice.deviceId, reason);
            setCurrentDevice((prev) => ({
                ...prev,
                failedAttempts: 0,
                activationAttempts: 0,
                isLocked: false,
                lockedUntil: null,
            }));
            setActionOk('Lockout resettato con successo.');
            setModal(null);
            onRefresh();
        } catch (e) {
            setActionErr(e?.response?.data?.error || 'Reset lockout fallito.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleSendInstructions = async ({ reason, deliveryMode, recipientEmail, phone, messageText }) => {
        setActionLoading(true);
        setActionErr('');
        try {
            await apiService.sendDeviceActivationInstructions(activeDevice.deviceId, {
                deliveryMode,
                reason,
                recipientEmail: deliveryMode === 'email' ? recipientEmail : undefined,
                phone: deliveryMode === 'sms' ? phone : undefined,
                messageText,
                pin: sessionPin || undefined,
            });
            if (deliveryMode === 'email') {
                setActionOk(`Email inviata a ${recipientEmail}.`);
            } else {
                setActionOk('Invio SMS registrato nel log.');
            }
            setModal(null);
        } catch (e) {
            setActionErr(e?.response?.data?.error || 'Invio istruzioni fallito.');
        } finally {
            setActionLoading(false);
        }
    };

    const Row = ({ label, value }) => (
        <div style={{ display: 'flex', gap: 8, padding: '7px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
            <span style={{ minWidth: 180, color: '#6b7280', fontWeight: 500 }}>{label}</span>
            <span style={{ color: '#111827', fontWeight: 400 }}>{value || '—'}</span>
        </div>
    );

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 8000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 580, width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <KeyRound size={18} /> {activeDevice.label || activeDevice.setupCode}
                    </h3>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><X size={18} /></button>
                </div>

                <ErrorBox message={actionErr} />
                <SuccessBox message={actionOk} />

                {/* Details */}
                <div style={{ background: '#f9fafb', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
                    <Row label="Device ID" value={<code style={{ fontSize: 11 }}>{activeDevice.deviceId}</code>} />
                    <Row label="Setup Code" value={<strong style={{ fontFamily: 'monospace' }}>{activeDevice.setupCode}</strong>} />
                    <Row label="Seriale" value={activeDevice.serialNumber} />
                    <Row label="Tenant" value={activeDevice.tenant?.company_name || activeDevice.tenant?.adminAlias} />
                    <Row label="Venue" value={activeDevice.venue?.name} />
                    <Row label="Stato Device" value={<StatusBadge status={activeDevice.activationStatus} />} />
                    <Row
                        label="Scaduto"
                        value={isPinExpired ? (
                            <span style={{ background: '#fee2e2', color: '#dc2626', padding: '2px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 600 }}>Sì</span>
                        ) : (
                            <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 4 }}>
                                <span style={{ background: '#dcfce7', color: '#16a34a', padding: '2px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 600, width: 'fit-content' }}>No</span>
                                <span style={{ fontSize: 11, color: '#6b7280' }}>
                                    {pinExpiry ? `Scade il ${fmtDate(pinExpiry)}` : 'Nessuna scadenza'}
                                </span>
                            </span>
                        )}
                    />
                    <Row label="Scadenza PIN" value={fmtDate(pinExpiry)} />
                    <Row label="Tentativi falliti" value={activeDevice.failedAttempts ?? activeDevice.activationAttempts ?? '—'} />
                    <Row label="Lockout" value={isLocked ? <span style={{ color: '#dc2626', fontWeight: 600 }}>🔒 Bloccato fino a {fmtDate(activeDevice.lockedUntil)}</span> : 'No'} />
                    <Row label="Ultima attivazione" value={fmtDate(activeDevice.lastActivationAt || activeDevice.verifiedAt)} />
                </div>

                {/* PIN one-time reveal */}
                {pinReveal && (
                    <PinRevealBox
                        pin={pinReveal.pin}
                        setupCode={pinReveal.setupCode}
                        expiresAt={pinReveal.expiresAt}
                        onClose={() => { setPinReveal(null); onRefresh(); }}
                    />
                )}

                {/* Actions */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                    {!isDisabled && (
                        <button
                            onClick={() => { setActionErr(''); setActionOk(''); setModal('regenerate'); }}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                        >
                            <RefreshCw size={14} /> Rigenera PIN
                        </button>
                    )}
                    {(isLocked || (activeDevice.failedAttempts > 0 || activeDevice.activationAttempts > 0)) && (
                        <button
                            onClick={() => { setActionErr(''); setActionOk(''); setModal('reset-lockout'); }}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                        >
                            <Unlock size={14} /> Reset Lockout
                        </button>
                    )}
                    <button
                        onClick={() => { setActionErr(''); setActionOk(''); setModal('send-instructions'); }}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                    >
                        <ClipboardCopy size={14} /> Istruzioni attivazione
                    </button>
                    <button
                        onClick={() => { setActionErr(''); setActionOk(''); setShowHistory(true); }}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, cursor: 'pointer' }}
                    >
                        <History size={14} /> Cronologia
                    </button>
                </div>
            </div>

            {/* Modals */}
            {modal === 'regenerate' && (
                <ConfirmModal
                    title="Rigenera PIN"
                    description={`Questa operazione invalida il PIN corrente di "${activeDevice.label || activeDevice.setupCode}" e ne genera uno nuovo. Il vecchio PIN non sarà più utilizzabile. Il nuovo PIN sarà mostrato una sola volta.`}
                    requireReason
                    confirmLabel="Rigenera"
                    confirmColor="#2563eb"
                    loading={actionLoading}
                    onConfirm={handleRegenerate}
                    onClose={() => setModal(null)}
                />
            )}
            {modal === 'reset-lockout' && (
                <ConfirmModal
                    title="Reset Lockout"
                    description={`Resetta i tentativi falliti e sblocca "${activeDevice.label || activeDevice.setupCode}" senza rigenerare il PIN.`}
                    requireReason
                    confirmLabel="Reset Lockout"
                    confirmColor="#f59e0b"
                    loading={actionLoading}
                    onConfirm={handleResetLockout}
                    onClose={() => setModal(null)}
                />
            )}
            {modal === 'send-instructions' && (
                <SendInstructionsModal
                    device={activeDevice}
                    sessionPin={sessionPin}
                    loading={actionLoading}
                    onConfirm={handleSendInstructions}
                    onClose={() => setModal(null)}
                />
            )}

            {showHistory && <HistoryPanel deviceId={activeDevice.deviceId} onClose={() => setShowHistory(false)} />}
        </div>
    );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function DeviceActivationSupport() {
    const [searchParams, setSearchParams] = useState({ search: '', tenantId: '', status: '' });
    const [results, setResults] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [sortKey, setSortKey] = useState(null);
    const [sortDir, setSortDir] = useState('asc');
    const [loading, setLoading] = useState(false);
    const [searchErr, setSearchErr] = useState('');
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [detailDevice, setDetailDevice] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailErr, setDetailErr] = useState('');

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDir('asc');
        }
    };

    const sortedResults = [...results].sort((a, b) => {
        if (!sortKey) return 0;
        let av, bv;
        if (sortKey === 'label') { av = (a.label || a.setupCode || '').toLowerCase(); bv = (b.label || b.setupCode || '').toLowerCase(); }
        else if (sortKey === 'tenant') { av = (a.tenant?.company_name || a.tenant?.adminAlias || '').toLowerCase(); bv = (b.tenant?.company_name || b.tenant?.adminAlias || '').toLowerCase(); }
        else if (sortKey === 'venue') { av = (a.venue?.name || '').toLowerCase(); bv = (b.venue?.name || '').toLowerCase(); }
        else if (sortKey === 'activationStatus') { av = a.activationStatus || ''; bv = b.activationStatus || ''; }
        else if (sortKey === 'setupCodeStatus') { av = a.setupCodeStatus || ''; bv = b.setupCodeStatus || ''; }
        else if (sortKey === 'isLocked') { av = a.isLocked ? 1 : 0; bv = b.isLocked ? 1 : 0; }
        else return 0;
        if (av < bv) return sortDir === 'asc' ? -1 : 1;
        if (av > bv) return sortDir === 'asc' ? 1 : -1;
        return 0;
    });

    const handleSearch = useCallback(async (e) => {
        e?.preventDefault();
        setLoading(true);
        setSearchErr('');
        setResults([]);
        try {
            const data = await apiService.searchDeviceActivation({
                search: searchParams.search || undefined,
                tenantId: searchParams.tenantId || undefined,
                status: searchParams.status || undefined,
            });
            setResults(data.results || []);
            setPagination(data.pagination || null);
        } catch (err) {
            setSearchErr(err?.response?.data?.error || 'Ricerca fallita.');
        } finally {
            setLoading(false);
        }
    }, [searchParams]);

    const openDetail = useCallback(async (deviceId) => {
        setDetailLoading(true);
        setDetailErr('');
        setDetailDevice(null);
        try {
            const data = await apiService.getDeviceActivationStatus(deviceId);
            setDetailDevice(data);
            setSelectedDevice(deviceId);
        } catch (err) {
            setDetailErr(err?.response?.data?.error || 'Impossibile caricare il dettaglio.');
        } finally {
            setDetailLoading(false);
        }
    }, []);

    const refreshDetail = useCallback(() => {
        if (selectedDevice) openDetail(selectedDevice);
        handleSearch();
    }, [selectedDevice, openDetail, handleSearch]);

    return (
        <div style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                <Lock size={24} color="#2563eb" />
                <div>
                    <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>Device Activation Support</h1>
                    <p style={{ margin: 0, color: '#6b7280', fontSize: 13 }}>Gestione problemi di attivazione tablet/kiosk — Setup Code + PIN</p>
                </div>
            </div>

            {/* Search bar */}
            <form onSubmit={handleSearch} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 20px', marginBottom: 20 }}>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                        <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4, color: '#374151' }}>Cerca (Setup Code, label, seriale)</label>
                        <input
                            type="text"
                            value={searchParams.search}
                            onChange={(e) => setSearchParams((p) => ({ ...p, search: e.target.value }))}
                            placeholder="es. ST-9AF7B2 oppure Tablet Ingresso"
                            style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                        />
                    </div>
                    <div style={{ minWidth: 180 }}>
                        <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4, color: '#374151' }}>Tenant ID</label>
                        <input
                            type="text"
                            value={searchParams.tenantId}
                            onChange={(e) => setSearchParams((p) => ({ ...p, tenantId: e.target.value }))}
                            placeholder="UUID tenant"
                            style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                        />
                    </div>
                    <div style={{ minWidth: 160 }}>
                        <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4, color: '#374151' }}>Status</label>
                        <select
                            value={searchParams.status}
                            onChange={(e) => setSearchParams((p) => ({ ...p, status: e.target.value }))}
                            style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                        >
                            <option value="">Tutti</option>
                            <option value="PROVISIONED">Provisionato</option>
                            <option value="SHIPPED_PENDING_ACTIVATION">Spedito</option>
                            <option value="ACTIVE">Attivo</option>
                            <option value="DISABLED">Disabilitato</option>
                        </select>
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                        {loading ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Search size={14} />}
                        Cerca
                    </button>
                </div>
            </form>

            <ErrorBox message={searchErr} />
            {detailErr && <ErrorBox message={detailErr} />}

            {/* Results */}
            {results.length > 0 && (
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{pagination?.total || results.length} dispositivi trovati</span>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#f9fafb' }}>
                                {[
                                    { label: 'Label / Setup Code', key: 'label' },
                                    { label: 'Tenant', key: 'tenant' },
                                    { label: 'Venue', key: 'venue' },
                                    { label: 'Stato Device', key: 'activationStatus' },
                                    { label: 'Scaduto', key: 'setupCodeStatus' },
                                    { label: 'Lockout', key: 'isLocked' },
                                    { label: 'Azioni', key: null },
                                ].map(({ label: h, key }) => (
                                    <th
                                        key={h}
                                        onClick={key ? () => handleSort(key) : undefined}
                                        style={{ padding: '10px 14px', fontSize: 12, fontWeight: 700, color: '#6b7280', textAlign: 'left', borderBottom: '1px solid #e5e7eb', cursor: key ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap' }}
                                    >
                                        {h}{key && (sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ↕')}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedResults.map((d) => (
                                <tr key={d.deviceId} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                    <td style={{ padding: '10px 14px' }}>
                                        <div style={{ fontWeight: 600, fontSize: 13 }}>{d.label || '—'}</div>
                                        <code style={{ fontSize: 11, color: '#6b7280' }}>{d.setupCode}</code>
                                    </td>
                                    <td style={{ padding: '10px 14px', fontSize: 12 }}>{d.tenant?.company_name || d.tenant?.adminAlias || '—'}</td>
                                    <td style={{ padding: '10px 14px', fontSize: 12 }}>{d.venue?.name || '—'}</td>
                                    <td style={{ padding: '10px 14px' }}><StatusBadge status={d.activationStatus} /></td>
                                    <td style={{ padding: '10px 14px' }}>
                                        {d.setupCodeStatus === 'PIN_EXPIRED' ? (
                                            <span
                                                title={d.activationExpiry ? `Scaduto il ${fmtDate(d.activationExpiry)}` : 'PIN scaduto'}
                                                style={{ background: '#fee2e2', color: '#dc2626', padding: '2px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 600 }}
                                            >Sì</span>
                                        ) : (
                                            <span
                                                title={d.activationExpiry ? `Scade il ${fmtDate(d.activationExpiry)}` : 'Nessuna scadenza'}
                                                style={{ background: '#dcfce7', color: '#16a34a', padding: '2px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 600 }}
                                            >No</span>
                                        )}
                                    </td>
                                    <td style={{ padding: '10px 14px' }}>
                                        {d.isLocked
                                            ? <span style={{ color: '#dc2626', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}><Lock size={12} /> Bloccato</span>
                                            : <span style={{ color: '#16a34a', fontSize: 12 }}>No</span>}
                                    </td>
                                    <td style={{ padding: '10px 14px' }}>
                                        <button
                                            onClick={() => openDetail(d.deviceId)}
                                            disabled={detailLoading}
                                            style={{ padding: '6px 14px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
                                        >
                                            {detailLoading && selectedDevice === d.deviceId ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : 'Gestisci →'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {results.length === 0 && !loading && pagination !== null && (
                <div style={{ textAlign: 'center', padding: 48, color: '#6b7280', fontSize: 14 }}>
                    <ShieldAlert size={32} style={{ marginBottom: 12, opacity: 0.4 }} />
                    <p>Nessun device trovato con i criteri specificati.</p>
                </div>
            )}

            {/* Detail panel */}
            {detailDevice && (
                <DeviceDetailPanel
                    device={detailDevice}
                    onClose={() => { setDetailDevice(null); setSelectedDevice(null); }}
                    onRefresh={refreshDetail}
                />
            )}

            {/* Inline spin keyframe */}
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}
